import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ageFrom } from '@/lib/age';
import { date, localeFormat } from '@/lib/date';
import { useTermInvite } from '@/services/term-acceptances';

export type CreatedSoldier = {
  id: string;
  name: string;
  birthday?: string | null;
  phone?: string | null;
  emergencyContactPhone?: string | null;
};

/** Só dígitos com DDI do Brasil; sem telefone válido o WhatsApp abre pra escolher o contato. */
const whatsappNumber = (...phones: (string | null | undefined)[]) => {
  for (const phone of phones) {
    const digits = (phone ?? '').replace(/\D/g, '');
    if (digits.length === 10 || digits.length === 11) return `55${digits}`;
    if (digits.length === 12 || digits.length === 13) return digits;
  }
  return '';
};

const buildMessage = (soldier: CreatedSoldier, url: string, expiresAt: string) => {
  const age = soldier.birthday ? ageFrom(String(soldier.birthday).slice(0, 10)) : null;
  const until = date(expiresAt).format(localeFormat);
  const first = soldier.name.split(' ')[0];

  const intro =
    age !== null && age < 18
      ? `Olá! A inscrição de ${soldier.name} no Aliste-se foi feita. Falta só um passo: o responsável precisa ler e aceitar os termos e a autorização.`
      : `Olá, ${first}! Sua inscrição no Aliste-se foi feita. Falta só um passo: ler e aceitar os termos.`;

  return `${intro}\n\nAcesse o link (vale até ${until}):\n${url}`;
};

/**
 * Aparece depois de cadastrar um recruta pelo admin: a inscrição feita pela secretaria ainda
 * não tem aceite dos termos, então já gera o link de aceite (`/termos/aceitar/<token>`) com a
 * mensagem pronta pra copiar ou mandar pelo WhatsApp.
 */
export function SoldierCreatedDialog({
  soldier,
  onList,
  onNew,
}: {
  soldier: CreatedSoldier | null;
  onList: () => void;
  onNew: () => void;
}) {
  const toast = useToast();
  const {
    mutate: createInvite,
    data: invite,
    isPending,
    isError,
    reset,
  } = useTermInvite();
  const [copied, setCopied] = useState<'link' | 'message' | null>(null);

  useEffect(() => {
    if (soldier) createInvite(soldier.id);
    else reset();
  }, [soldier, createInvite, reset]);

  const message =
    soldier && invite ? buildMessage(soldier, invite.url, invite.expiresAt) : '';

  const copy = async (what: 'link' | 'message') => {
    await navigator.clipboard.writeText(what === 'link' ? (invite?.url ?? '') : message);
    setCopied(what);
    toast.success(what === 'link' ? 'Link copiado!' : 'Mensagem copiada!');
    setTimeout(() => setCopied(null), 1500);
  };

  const openWhatsapp = () => {
    if (!soldier) return;
    const number = whatsappNumber(soldier.phone, soldier.emergencyContactPhone);
    window.open(
      `https://wa.me/${number}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener',
    );
  };

  return (
    <Dialog open={!!soldier} onOpenChange={(open) => !open && onList()}>
      <DialogContent className="max-w-[520px] gap-5 overflow-hidden rounded-[20px] p-0">
        <div className="flex flex-col items-center gap-3 border-b border-border bg-[radial-gradient(circle_at_50%_0%,rgba(122,163,74,.16),transparent_70%)] px-8 pb-6 pt-9 text-center">
          <span className="flex size-14 items-center justify-center rounded-full border border-olive-line bg-accent text-olive-icon">
            <Icon name="check-check" className="size-6" />
          </span>
          <DialogTitle className="font-display text-[22px] font-extrabold uppercase tracking-tight text-white">
            Recruta cadastrado!
          </DialogTitle>
          <DialogDescription className="m-0 max-w-[40ch] text-[13.5px] leading-relaxed text-muted-foreground">
            <strong className="text-foreground">{soldier?.name}</strong> já está na lista.
            Falta o aceite dos termos: envie o link abaixo para o participante (ou o
            responsável, se for menor).
          </DialogDescription>
        </div>

        <div className="flex flex-col gap-4 px-8">
          {isPending && (
            <p className="m-0 flex items-center gap-2 text-[13px] text-muted-foreground">
              <Icon name="loader-circle" className="size-4 animate-spin" />
              Gerando o link dos termos…
            </p>
          )}

          {isError && (
            <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-border p-4">
              <p className="m-0 text-[13px] text-muted-foreground">
                Não foi possível gerar o link agora. O recruta foi salvo — você pode gerar
                o link depois, na ficha dele.
              </p>
              <Button
                type="button"
                variant="outline"
                size="md"
                icon="rotate-cw"
                onClick={() => soldier && createInvite(soldier.id)}
              >
                Tentar de novo
              </Button>
            </div>
          )}

          {invite && (
            <>
              <div className="flex items-center gap-2">
                <Input readOnly value={invite.url} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={copied === 'link' ? 'text-green-500' : undefined}
                  onClick={() => copy('link')}
                  aria-label="Copiar link"
                >
                  <Icon name={copied === 'link' ? 'check-check' : 'copy'} />
                </Button>
              </div>

              <p className="m-0 whitespace-pre-line rounded-xl border border-border bg-field px-4 py-3 text-[13px] leading-relaxed text-muted-foreground">
                {message}
              </p>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="sand"
                  size="md"
                  icon="message-circle"
                  onClick={openWhatsapp}
                >
                  Enviar pelo WhatsApp
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  icon={copied === 'message' ? 'check-check' : 'copy'}
                  onClick={() => copy('message')}
                >
                  Copiar mensagem
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border px-8 py-4">
          <Button type="button" variant="outline" size="md" onClick={onNew}>
            Cadastrar outro
          </Button>
          <Button type="button" variant="outline" size="md" icon="list" onClick={onList}>
            Ir para a listagem
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
